import { ChangeEvent, useState, useEffect } from 'react';
import round from 'lodash/round';
import { AutomatedBreakType } from '@sdk/settings/types';
import { AutomatedBreakPaidCategory } from '@sdk/automatedBreaks/types/AutomatedBreakPaidCategory';

import { TabType } from '@web/settings/types';
import { AutomatedBreakFormFields } from '@web/settings/components/schedule/Breaks/breaks.types';
import {
  getFieldName,
  getSafeNumber,
} from '@web/settings/components/schedule/Breaks/AutomatedBreak/automatedBreak.utils';
import { useSettingsErrorsContext } from '@web/settings/SettingsErrorContext';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import {
  minutesValidator,
  hoursValidator,
  breakDurationLessThanBreakThresholdValidator,
  breakThresholdGreaterThanBreakDurationValidator,
  validateIsNumberMultipleOfHalfHour,
  hasAtMostOneDecimalPlace,
} from '@web/settings/components/schedule/Breaks/breaks.utils';

type Props = {
  fields: AutomatedBreakFormFields;
  automatedBreakType: AutomatedBreakType;
};

export const useAutomatedBreakFields = ({ fields, automatedBreakType }: Props) => {
  const { updateAutomatedBreaksSettings, automatedBreaks: automatedBreaksSettings } =
    useAutomatedBreaksSettings();
  const { toggleError } = useSettingsErrorsContext();
  const [hasError, setHasError] = useState(
    !!fields.breakDuration.error || !!fields.breakThreshold.error
  );

  useEffect(() => {
    const fieldName = getFieldName(automatedBreakType, 'breakDuration');
    toggleError(TabType.schedule, fieldName, !!fields.breakDuration.runValidation());
  }, [fields.breakDuration.value]);

  useEffect(
    () => {
      const fieldName = getFieldName(automatedBreakType, 'breakThreshold');
      toggleError(TabType.schedule, fieldName, !!fields.breakThreshold.runValidation());
    },
    automatedBreakType === AutomatedBreakType.meal
      ? [
          automatedBreakType,
          fields.breakThreshold.value,
          fields.breakThreshold.defaultValue,
          fields.isWaivingEnabled.value,
          fields.waivingMode.value,
          fields.waivingFirstBreakMode.value,
          fields.waivingFirstBreakLessThanHours.value,
          fields.waivingFirstBreakLessThanHours.defaultValue,
          fields.waivingSecondBreakMode.value,
          fields.waivingSecondBreakLessThanHours.value,
          fields.waivingSecondBreakLessThanHours.defaultValue,
        ]
      : [automatedBreakType, fields.breakThreshold.value]
  );

  useEffect(() => {
    setHasError(!!fields.breakDuration.error || !!fields.breakThreshold.error);
  }, [fields.breakDuration, fields.breakThreshold]);

  const updatePaid = (paid: AutomatedBreakPaidCategory) => {
    const fieldName = getFieldName(automatedBreakType, 'isPaid');
    fields.isPaid.onChange(paid);
    updateAutomatedBreaksSettings({ [fieldName]: paid === AutomatedBreakPaidCategory.paid });
  };

  const updateBreakDuration = (event: ChangeEvent<HTMLInputElement>) => {
    const fieldName = getFieldName(automatedBreakType, 'breakDuration');
    const breakDuration = getSafeNumber(event.currentTarget.value);
    fields.breakDuration.onChange(event.currentTarget.value);
    updateAutomatedBreaksSettings({ [fieldName]: breakDuration });
  };

  const updateBreakThreshold = (event: ChangeEvent<HTMLInputElement>) => {
    if (hasAtMostOneDecimalPlace(event.currentTarget.value)) {
      const fieldName = getFieldName(automatedBreakType, 'breakThreshold');
      const breakThresholdHours = getSafeNumber(event.currentTarget.value);
      fields.breakThreshold.onChange(event.currentTarget.value);

      const roundedMinutes = round(breakThresholdHours * 60);
      if (automatedBreaksSettings[automatedBreakType].breakThreshold !== roundedMinutes) {
        updateAutomatedBreaksSettings({ [fieldName]: roundedMinutes });
      }
    }
  };

  const updateEarlyReturn = (isEarlyReturn: boolean) => {
    const fieldName = getFieldName(automatedBreakType, 'isEarlyReturn');
    fields.isEarlyReturn.onChange(isEarlyReturn);
    updateAutomatedBreaksSettings({ [fieldName]: isEarlyReturn });
  };

  const updatePremiumPay = (isPremiumPay: boolean) => {
    const fieldName = getFieldName(automatedBreakType, 'isPremiumPay');
    fields.isPremiumPay.onChange(isPremiumPay);
    updateAutomatedBreaksSettings({ [fieldName]: isPremiumPay });
  };

  return {
    hasError,
    updatePaid,
    updateBreakDuration,
    updateBreakThreshold,
    updateEarlyReturn,
    updatePremiumPay,
    isAttestationEnabled: automatedBreaksSettings.isAttestation,
    errorText: fields.breakDuration.error || fields.breakThreshold.error,
  };
};
